import { useState } from "react";
import { ChevronDownIcon, StickyNoteIcon } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { Badge } from "@component/badge";
import { cn } from "@util/tailwind";
import { convertToLowerSnakeCase, getApplicantStatusBadge } from "@util/string";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { parseFormat } from "@util/date";
import { Tooltip } from "./tooltip";
import { ApplicantOfferMetadata } from "@type/job";

interface DropdownApplicantStatus {
    isDisabledPopover?: boolean;
    isStaticDisplay?: boolean;
    /** Disables the trigger so the dropdown can't open. Visual style of the
     *  badge is preserved — only `pointer-events`/native `disabled` change. */
    disabled?: boolean;
    currentStatus: string;
    onStatusChange: (newStatus: string) => void;
    className?: string;
    isOffersTab?: boolean;
    isIntakeTab?: boolean;
    offerNotes?: string;
    offerMetadata?: ApplicantOfferMetadata | undefined
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    upload_complete: {
        label: "Upload Complete",
        className:
            "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40",
    },
    cv_parsed: {
        label: "CV Parsed",
        className:
            "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40",
    },
    shortlisted: {
        label: "Shortlisted",
        className:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/40",
    },
    on_hold: {
        label: "On Hold",
        className:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/40",
    },
    not_suitable: {
        label: "Not Suitable",
        className:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/40",
    },
    pending_review: {
        label: "Pending Review",
        className:
            "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40",
    },
    pending_decision: {
        label: "Pending Decision",
        className:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/40",
    },
    review_scheduled: {
        label: "Review Scheduled",
        className:
            "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/40",
    },
    offer_sent: {
        label: "Offer Sent",
        className:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/40",
    },
    offer_resent: {
        label: "Offer Resent",
        className:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/40",
    },
    offer_declined: {
        label: "Offer Declined",
        className:
            "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800/40",
    },
};

const NEEDS_REVIEW_STATUSES = [
    "review_scheduled",
    "shortlisted",
    "pending_decision",
    "not_suitable",
];

const OFFERS_STATUSES = [
    "offer_resent",
    "offer_declined"
];

const INTAKE_STATUSES: Array<{ value: string; label: string; description: string }> = [
    { value: "shortlisted", label: "Shortlisted", description: "CV looks good. Ready to send a screening invitation." },
    { value: "on_hold", label: "On Hold", description: "Not the right time to progress. Candidate is kept for future consideration." },
    { value: "not_suitable", label: "Not Suitable", description: "Does not meet requirements for this role." },
];

const STATUS_ITEM_CONFIG: Record<string, { label: string; description: string }> = {
    pending_decision: { label: "Pending Decision", description: "Review completed. Team is still deliberating on next steps." },
    review_scheduled: { label: "Review Scheduled", description: "A follow-up review or interview session has been lined up." },
    shortlisted: { label: "Shortlisted", description: "Candidate is a strong hire. Ready for a job offer." },
    not_suitable: { label: "Not Suitable", description: "Does not meet requirements for this role." },
    offer_resent: { label: "Offer Resent", description: "Record that the offer was sent again." },
    offer_declined: { label: "Offer Declined", description: "Candidate has turned down the offer." },
};

export default function StatusDropdown({
    isDisabledPopover = false,
    isStaticDisplay = false,
    disabled = false,
    currentStatus,
    onStatusChange,
    className,
    isOffersTab = false,
    isIntakeTab = false,
    offerNotes = "",
    offerMetadata = undefined
}: DropdownApplicantStatus) {
    const [isOpen, setIsOpen] = useState(false);

    if (isStaticDisplay) {
        const applicantStatus = getApplicantStatusBadge(currentStatus);
        const staticClassName = applicantStatus.className.split(' ').filter(cls => !cls.startsWith('hover:')).join(' ');
        return (
            <Badge
                variant="outline"
                className={cn("whitespace-nowrap inline-flex items-center gap-1.5 text-sm", staticClassName)}
                style={{ paddingTop: "4px", paddingBottom: "4px", paddingLeft: "8px", paddingRight: "8px", border: "0.909091px solid rgba(0, 0, 0, 0)", borderRadius: "1rem", boxShadow: "none" }}
            >
                {applicantStatus.label}
            </Badge>
        );
    }

    const currentStatusConfig = STATUS_CONFIG[currentStatus] || {
        label: currentStatus
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        className:
            "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40",
    };

    const handleStatusChange = (newStatus: string) => {
        if (newStatus === currentStatus && currentStatus === "offer_resent") return;
        onStatusChange(newStatus);
        setIsOpen(false);
    };

    const availableStatuses = isOffersTab ? OFFERS_STATUSES : NEEDS_REVIEW_STATUSES;

    // Intake tabs (uploads/applied): show a dropdown with text-style items
    if (isIntakeTab && ["upload_complete", "cv_parsed", "shortlisted", "on_hold", "not_suitable"].includes(currentStatus)) {
        const applicantStatus = getApplicantStatusBadge(currentStatus);
        return (
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild disabled={disabled}>
                    <button
                        className={cn("inline-flex items-center transition-colors", className)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                    >
                        <Badge
                            className={cn(applicantStatus.className, "whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5")}
                            style={{ paddingTop: "4px", paddingBottom: "4px", paddingLeft: "8px", paddingRight: "8px", border: "0.909091px solid rgba(0, 0, 0, 0)", borderRadius: "1rem", boxShadow: "none" }}
                        >
                            {applicantStatus.label}
                            <ChevronDownIcon className="w-3.5 h-3.5" />
                        </Badge>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                    {INTAKE_STATUSES.filter((item) => item.value !== currentStatus).map((item) => (
                        <DropdownMenuItem
                            key={item.value}
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(item.value); }}
                            className="flex items-start gap-3 p-3 cursor-pointer"
                        >
                            <div className="flex-1">
                                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.label}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</div>
                            </div>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    if (["hired", "rejected", "assessment_rejected", "cv_rejected", "ai_interview_rejected", "offer_rejected", "cv_parsed", "pending_assessment", "partial_interview", "aborted_interview", "link_expired", "archived", "upload_complete"].includes(currentStatus)) {
        const applicantStatus = getApplicantStatusBadge(currentStatus)
        const staticClassName = applicantStatus.className.split(' ').filter(cls => !cls.startsWith('hover:')).join(' ');
        return (
            <Badge
                variant="outline"
                className={cn("whitespace-nowrap inline-flex items-center gap-1.5 text-sm", staticClassName)}
                style={{
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    border:
                        "0.909091px solid rgba(0, 0, 0, 0)",
                    borderRadius: "1rem",
                    boxShadow: "none",
                }}
            >
                {applicantStatus.label}
            </Badge>
        );
    }

    if (!isDisabledPopover && ["offer_sent", "offer_resent"].includes(convertToLowerSnakeCase(currentStatus))) {
        return (
            <div className="flex justify-center items-center gap-2">
                <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                    <DropdownMenuTrigger asChild disabled={disabled}>
                        <button
                            className={cn(
                                "inline-flex items-center transition-colors",
                                className
                            )}
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                        >
                            <Badge
                                className={cn(
                                    currentStatusConfig.className,
                                    "whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                                )}
                                style={{
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                    border: "0.909091px solid rgba(0, 0, 0, 0)",
                                    borderRadius: "1rem",
                                    boxShadow: "none",
                                }}
                            >
                                {currentStatusConfig.label}
                                <ChevronDownIcon className="w-3.5 h-3.5" />
                            </Badge>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                        {availableStatuses.filter((status) => status !== currentStatus).map((status) => {
                            const itemConfig = STATUS_ITEM_CONFIG[status];
                            return (
                                <DropdownMenuItem
                                    key={status}
                                    onClick={(e) => { e.stopPropagation(); handleStatusChange(status); }}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{itemConfig?.label ?? status}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{itemConfig?.description}</div>
                                    </div>
                                </DropdownMenuItem>
                            );
                        })}
                    </DropdownMenuContent>
                </DropdownMenu>
   
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center cursor-pointer p-1 rounded">
                            <Tooltip
                                side="top"
                                content={(
                                    <p>View internal offer note</p>
                                )}
                            >
                                <span className="text-sm text-blue-600 dark:text-blue-400 underline cursor-pointer hover:text-black dark:hover:text-black">
                                    View
                                </span>
                            </Tooltip>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-96"
                        align="center"
                    >
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                                    <StickyNoteIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                    Internal Offer Note
                                </h4>
                                <div className="space-y-1.5">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                            Offer sent by:
                                        </span>{" "}
                                        {offerMetadata?.offered_by || "N/A"}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                            Date:
                                        </span>{" "}
                                        {offerMetadata?.offered_at ? (
                                            <>
                                                {parseFormat(offerMetadata?.offered_at || "", "D MMM YYYY")} at {parseFormat(offerMetadata?.offered_at || "", "h:mm A")}
                                            </>
                                        ) : "N/A"}
                                    </p>
                                </div>
                            </div>
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                    {offerNotes || "N/A"}
                                </p>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        )
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild disabled={disabled}>
                <button
                    className={cn(
                        "inline-flex items-center transition-colors",
                        className
                    )}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                >
                    <Badge
                        className={cn(
                            currentStatusConfig.className,
                            "whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 ring-1 ring-transparent hover:ring-gray-300 dark:hover:ring-gray-600 transition-all"
                        )}
                        style={{
                            paddingTop: "4px",
                            paddingBottom: "4px",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                            border: "0.909091px solid rgba(0, 0, 0, 0)",
                            borderRadius: "1rem",
                            boxShadow: "none",
                        }}
                    >
                        {currentStatusConfig.label}
                        <ChevronDownIcon className="w-4 h-4 opacity-70" />
                    </Badge>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                {availableStatuses.filter((status) => status !== currentStatus).map((status) => {
                    const itemConfig = STATUS_ITEM_CONFIG[status];
                    return (
                        <DropdownMenuItem
                            key={status}
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(status); }}
                            className="flex items-start gap-3 p-3 cursor-pointer"
                        >
                            <div className="flex-1">
                                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{itemConfig?.label ?? status}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{itemConfig?.description}</div>
                            </div>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
